import type { PostHogInterface } from 'posthog-js'

/**
 * Which www.maayot.com page sent a visitor into the app — and which page their
 * visit to www started on.
 *
 * PostHog's own `$referrer` cannot answer this. Browsers cut a cross-origin
 * referrer down to the origin, so every visit from the blog arrives here as
 * `https://www.maayot.com/` — 127 posts collapse into one row.
 *
 * So the www side says it explicitly. The snippet in
 * `docs/seo/www-tracking-snippet.html` appends three params to every link into
 * story.maayot.com:
 *
 * - `ref_page`    the www path the link was clicked on
 * - `ref_landing` the www path this visit started on. Blog posts mostly link to
 *                 the homepage, not the app, so a reader goes post → homepage →
 *                 signup and `ref_page` alone would say "/" every time.
 * - `ref_cta`     which link: placement plus its text, e.g. `header:sign-up`
 *
 * This reads them back and attaches them to the visitor:
 *
 * - as session super properties, so the `sign_up` fired later in the same visit
 *   carries them and a funnel can be broken down by blog post, and
 * - as person properties — first touch set once, last touch overwritten — so
 *   trials and payments days later, including backend-sent ones, still carry them.
 */

/** Longest value kept. Paths and link labels are short; anything longer is junk or abuse. */
const MAX_LENGTH = 200

export interface SiteReferral {
  page: string
  landing: string | null
  cta: string | null
}

function clean(value: string | null): string | null {
  if (!value) return null
  const trimmed = value.trim().slice(0, MAX_LENGTH)
  return trimmed || null
}

/** Parse the www referral params from a query string. Null when there is no `ref_page`. */
export function readSiteReferral(search: string): SiteReferral | null {
  const params = new URLSearchParams(search)
  const page = clean(params.get('ref_page'))
  if (!page) return null
  return { page, landing: clean(params.get('ref_landing')), cta: clean(params.get('ref_cta')) }
}

/** Attach the www referral on this URL, if any, to the visitor. Call once PostHog has loaded. */
export const recordSiteReferral = (posthog: PostHogInterface) => {
  try {
    const referral = readSiteReferral(window.location.search)
    if (!referral) return

    posthog.register_for_session({
      site_ref_page: referral.page,
      site_ref_landing: referral.landing,
      site_ref_cta: referral.cta,
    })
    posthog.setPersonProperties(
      {
        last_site_ref_page: referral.page,
        last_site_ref_landing: referral.landing,
        last_site_ref_cta: referral.cta,
      },
      {
        first_site_ref_page: referral.page,
        first_site_ref_landing: referral.landing,
        first_site_ref_cta: referral.cta,
      },
    )
  } catch {
    /* analytics must never break the app */
  }
}
